import React, { useRef, useState, useEffect } from 'react';
import { formatTime } from '../../../utils/formatTime.mjs';
import { StyledContainer, StyledProgressBarContainer, StyledLoadTimeProgress, StyledTooltip, StyledCurrentTimeProgress } from './ProgressBar.styles.mjs';

const ProgressBar = ({ value, duration, secondaryValue = 0, isLive = false, onClick }) => {
    const progressBarRef = useRef(null);
    const overlayRef = useRef(null);
    const [showCaret, setShowCaret] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState(null);
    const [tooltipTime, setTooltipTime] = useState('');
    const [currentTimeBarWidth, setCurrentTimeBarWidth] = useState(0);
    const [isRewinding, setIsRewinding] = useState(false);
    useEffect(() => {
        if (progressBarRef.current) {
            const newWidth = Math.min(value * progressBarRef.current.offsetWidth, progressBarRef.current.offsetWidth);
            setCurrentTimeBarWidth(newWidth >= 0 ? newWidth : 0);
        }
    }, [value, progressBarRef]);
    const handleRewindStart = () => setIsRewinding(true);
    const handleRewindEnd = () => setIsRewinding(false);
    const handleToggleShowCaret = () => {
        setShowCaret(prev => !prev);
        handleRewindEnd();
    };
    const calculateSeekTime = (event) => {
        if (overlayRef.current) {
            const clientX = 'touches' in event ? event.touches[0].clientX : event.nativeEvent.offsetX;
            const progressBarWidth = overlayRef.current.offsetWidth;
            return (clientX / progressBarWidth) * duration;
        }
        return 0;
    };
    const handleRewind = (event) => {
        if (!isRewinding || !progressBarRef.current)
            return;
        const clientX = 'changedTouches' in event ? event.changedTouches[0].clientX : event.nativeEvent.offsetX;
        if (clientX <= progressBarRef.current.offsetWidth && clientX >= 0) {
            setCurrentTimeBarWidth(clientX);
            setTooltipPosition(clientX);
            onClick(calculateSeekTime(event));
            duration &&
                clientX &&
                progressBarRef.current.offsetWidth &&
                setTooltipTime(isLive
                    ? formatTime(((duration - value) * (progressBarRef.current.offsetWidth - clientX)) /
                        progressBarRef.current.offsetWidth, isLive)
                    : formatTime((duration * clientX) / progressBarRef.current.offsetWidth));
        }
    };
    const handleMouseMove = (event) => {
        if (isRewinding) {
            handleRewind(event);
        }
        else {
            const { offsetX } = event.nativeEvent;
            if (offsetX <= progressBarRef.current.offsetWidth && offsetX >= 0) {
                setTooltipPosition(offsetX);
                duration &&
                    offsetX &&
                    progressBarRef.current.offsetWidth &&
                    setTooltipTime(isLive
                        ? formatTime(((duration - value) * (progressBarRef.current.offsetWidth - offsetX)) /
                            progressBarRef.current.offsetWidth, isLive)
                        : formatTime((duration * offsetX) / progressBarRef.current.offsetWidth));
            }
        }
    };
    const handleTouchMove = event => {
        var _a;
        handleRewindStart();
        handleRewind(event);
        const offsetX = (_a = event.changedTouches[0]) === null || _a === void 0 ? void 0 : _a.clientX;
        if (offsetX && offsetX <= progressBarRef.current.offsetWidth && offsetX >= 0) {
            setTooltipPosition(offsetX);
            duration &&
                offsetX &&
                progressBarRef.current.offsetWidth &&
                setTooltipTime(isLive
                    ? formatTime(((duration - value) * (progressBarRef.current.offsetWidth - offsetX)) /
                        progressBarRef.current.offsetWidth, isLive)
                    : formatTime((duration * offsetX) / progressBarRef.current.offsetWidth));
        }
    };
    const handleClick = (event) => {
        const seekTime = calculateSeekTime(event);
        onClick(seekTime);
    };
    const handleMouseLeave = () => {
        handleToggleShowCaret();
        setTooltipPosition(null);
    };
    return (React.createElement(StyledContainer, { ref: overlayRef, onClick: handleClick, onMouseDown: handleRewindStart, onMouseEnter: handleToggleShowCaret, onMouseLeave: handleMouseLeave, onMouseMove: handleMouseMove, onTouchMove: handleTouchMove, onMouseUp: handleRewindEnd },
        React.createElement(StyledProgressBarContainer, { ref: progressBarRef },
            React.createElement(StyledLoadTimeProgress, { secondaryValue: secondaryValue, max: 100, className: "load-time-bar" }),
            Boolean(tooltipPosition) && (React.createElement(StyledTooltip, { tooltipPosition: tooltipPosition, showCaret: showCaret, className: "tooltip" }, tooltipTime)),
            React.createElement(StyledCurrentTimeProgress, { width: currentTimeBarWidth, showCaret: showCaret, className: "current-time-bar" }))));
};

export { ProgressBar as default };
//# sourceMappingURL=ProgressBar.mjs.map
